import { cn } from '@/lib/utils';
import { Canvas, useFrame } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

const COUNT = 512;
const CONNECTION_DISTANCE = 1.0;
const MOVEMENT_SPEED = 0.002;
const CURSOR_RADIUS = 3;
const HEX_SIZE = 0.5;
const ATTRACTION_STRENGTH = 0.03;
const POINT_SIZE = 0.08;
const HERO_ELEMENT_ID = 'auth-title';
const HERO_TRIGGER_DISTANCE = 0.4;
const CRYSTAL_TRIGGER_CHANCE = 0.5;
const CRYSTAL_DURATION_SECONDS = 5;
const CRYSTAL_RADIUS = 1.6;
const CRYSTAL_STRENGTH = 0.12;
const CRYSTAL_DAMPING = 0.5;
const CRYSTAL_EJECTION_MULTIPLIER = 1.4;
const CRYSTAL_MIN_EJECTION_SPEED = 0.02;
const CRYSTAL_EJECTION_GRACE_PERIOD = 0.5;
const GRID_SNAP_STRENGTH = 0.08;
const GRID_SNAP_DAMPING = 0.7;

function getCircleTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const context = canvas.getContext('2d');
  if (context) {
    context.beginPath();
    context.arc(16, 16, 14, 0, 2 * Math.PI);
    context.fillStyle = 'white';
    context.fill();
  }
  return new THREE.CanvasTexture(canvas);
}

function axialRound(q: number, r: number) {
  const x = q;
  const z = r;
  const y = -x - z;

  let rx = Math.round(x);
  let rz = Math.round(z);
  const ry = Math.round(y);

  const x_diff = Math.abs(rx - x);
  const z_diff = Math.abs(rz - z);
  const y_diff = Math.abs(ry - y);

  if (x_diff > y_diff && x_diff > z_diff) {
    rx = -ry - rz;
  } else if (y_diff > z_diff) {
    // y has largest error — rx and rz are already correct (ry is unused since we return axial [rx, rz])
  } else {
    rz = -rx - ry;
  }
  return [rx, rz];
}

function hexToPixel(q: number, r: number) {
  const x = HEX_SIZE * ((3 / 2) * q);
  const y = HEX_SIZE * ((Math.sqrt(3) / 2) * q + Math.sqrt(3) * r);
  return [x, y];
}

function pixelToHex(x: number, y: number) {
  const q = ((2 / 3) * x) / HEX_SIZE;
  const r = ((-1 / 3) * x + (Math.sqrt(3) / 3) * y) / HEX_SIZE;
  return [q, r];
}

function createParticleData() {
  const positions = new Float32Array(COUNT * 3);
  const velocities = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 25;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 25;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 10;
    velocities[i * 3] = (Math.random() - 0.5) * MOVEMENT_SPEED;
    velocities[i * 3 + 1] = (Math.random() - 0.5) * MOVEMENT_SPEED;
    velocities[i * 3 + 2] = (Math.random() - 0.5) * MOVEMENT_SPEED;
  }
  return {
    positions,
    velocities,
    linePositions: new Float32Array(COUNT * COUNT * 3),
  };
}

const PARTICLE_DATA = createParticleData();

function Particles({ heroHovered }: { heroHovered: boolean }) {
  const pointsRef = useRef<THREE.Points>(null);
  const linesRef = useRef<THREE.LineSegments>(null);
  const pointerRef = useRef(new THREE.Vector2(0, 0));
  const mouseVector = useRef(new THREE.Vector3());
  const pointerWorldRef = useRef(new THREE.Vector3());
  const heroCenterRef = useRef<THREE.Vector2 | null>(null);
  const heroVector = useRef(new THREE.Vector3());
  const heroWorldRef = useRef(new THREE.Vector3());
  const crystalActiveRef = useRef(false);
  const crystalExpiryRef = useRef(0);
  const crystalEjectionTimeRef = useRef(0);
  const crystalCenterRef = useRef(new THREE.Vector3());
  const crystallizedFlagsRef = useRef(new Uint8Array(COUNT));
  const arrivalVelocitiesRef = useRef(new Float32Array(COUNT * 3));
  const heroHoveredRef = useRef(heroHovered);
  heroHoveredRef.current = heroHovered;

  const texture = useMemo(() => getCircleTexture(), []);

  const { positions, velocities, linePositions } = PARTICLE_DATA;

  useEffect(() => {
    function handlePointerMove(event: PointerEvent) {
      const width = window.innerWidth || 1;
      const height = window.innerHeight || 1;
      pointerRef.current.x = (event.clientX / width) * 2 - 1;
      pointerRef.current.y = -(event.clientY / height) * 2 + 1;
    }

    window.addEventListener('pointermove', handlePointerMove);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
    };
  }, []);

  useEffect(() => {
    function updateHeroPosition() {
      const heroElement = document.getElementById(HERO_ELEMENT_ID);
      if (!heroElement) {
        heroCenterRef.current = null;
        return;
      }

      const rect = heroElement.getBoundingClientRect();
      const width = window.innerWidth || 1;
      const height = window.innerHeight || 1;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      heroCenterRef.current = new THREE.Vector2(
        (centerX / width) * 2 - 1,
        -(centerY / height) * 2 + 1,
      );
    }

    function handleScroll() {
      updateHeroPosition();
    }

    updateHeroPosition();
    window.addEventListener('resize', updateHeroPosition);
    window.addEventListener('scroll', handleScroll, { passive: true });

    let resizeObserver: ResizeObserver | null = null;
    const heroElement = document.getElementById(HERO_ELEMENT_ID);
    if (heroElement && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateHeroPosition());
      resizeObserver.observe(heroElement);
    }

    return () => {
      window.removeEventListener('resize', updateHeroPosition);
      window.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
    };
  }, []);

  useFrame((state) => {
    if (!pointsRef.current || !linesRef.current) return;

    mouseVector.current.set(pointerRef.current.x, pointerRef.current.y, 0);
    mouseVector.current.unproject(state.camera);
    const pointerDirection = mouseVector.current
      .sub(state.camera.position)
      .normalize();
    const pointerDistance =
      pointerDirection.z !== 0
        ? -state.camera.position.z / pointerDirection.z
        : 0;
    pointerWorldRef.current
      .copy(state.camera.position)
      .add(pointerDirection.multiplyScalar(pointerDistance));
    const mousePos = pointerWorldRef.current;

    let heroWorldPos: THREE.Vector3 | null = null;
    if (heroCenterRef.current) {
      heroVector.current.set(
        heroCenterRef.current.x,
        heroCenterRef.current.y,
        0,
      );
      heroVector.current.unproject(state.camera);
      const heroDirection = heroVector.current
        .sub(state.camera.position)
        .normalize();
      const heroDistance =
        heroDirection.z !== 0 ? -state.camera.position.z / heroDirection.z : 0;
      heroWorldRef.current
        .copy(state.camera.position)
        .add(heroDirection.multiplyScalar(heroDistance));
      heroWorldPos = heroWorldRef.current;
    }

    const crystallizedFlags = crystallizedFlagsRef.current;
    const arrivalVelocities = arrivalVelocitiesRef.current;

    let crystalExpiredThisFrame = false;
    if (
      crystalActiveRef.current &&
      state.clock.elapsedTime > crystalExpiryRef.current
    ) {
      crystalActiveRef.current = false;
      crystalExpiredThisFrame = true;
      crystalEjectionTimeRef.current = state.clock.elapsedTime;
    }

    const currentPositions = positions;
    const currentLinePositions = linePositions;

    let vertexIndex = 0;

    for (let i = 0; i < COUNT; i++) {
      let x = currentPositions[i * 3];
      let y = currentPositions[i * 3 + 1];
      let z = currentPositions[i * 3 + 2];

      const dxMouse = x - mousePos.x;
      const dyMouse = y - mousePos.y;
      const distToMouse = Math.sqrt(dxMouse * dxMouse + dyMouse * dyMouse);

      if (heroWorldPos) {
        const dxHero = x - heroWorldPos.x;
        const dyHero = y - heroWorldPos.y;
        const distToHero = Math.sqrt(dxHero * dxHero + dyHero * dyHero);
        if (
          !crystalActiveRef.current &&
          !crystalExpiredThisFrame &&
          state.clock.elapsedTime >
            crystalEjectionTimeRef.current + CRYSTAL_EJECTION_GRACE_PERIOD &&
          distToHero < HERO_TRIGGER_DISTANCE &&
          Math.random() < CRYSTAL_TRIGGER_CHANCE
        ) {
          crystalActiveRef.current = true;
          crystalExpiryRef.current =
            state.clock.elapsedTime + CRYSTAL_DURATION_SECONDS;
          crystalCenterRef.current.copy(heroWorldPos);
          crystallizedFlags.fill(0);
          arrivalVelocities.fill(0);
        }
      }

      let inCrystalZone = false;
      let justEjected = false;

      if (crystalActiveRef.current) {
        const dxCrystal = x - crystalCenterRef.current.x;
        const dyCrystal = y - crystalCenterRef.current.y;
        const distToCrystal = Math.sqrt(
          dxCrystal * dxCrystal + dyCrystal * dyCrystal,
        );
        if (distToCrystal < CRYSTAL_RADIUS) {
          const [q, r] = pixelToHex(x, y);
          const [rq, rr] = axialRound(q, r);
          const [targetX, targetY] = hexToPixel(rq, rr);

          if (!crystallizedFlags[i]) {
            crystallizedFlags[i] = 1;
            arrivalVelocities[i * 3] = velocities[i * 3];
            arrivalVelocities[i * 3 + 1] = velocities[i * 3 + 1];
            arrivalVelocities[i * 3 + 2] = velocities[i * 3 + 2];
          }

          velocities[i * 3] += (targetX - x) * CRYSTAL_STRENGTH;
          velocities[i * 3 + 1] += (targetY - y) * CRYSTAL_STRENGTH;
          velocities[i * 3 + 2] += (0 - z) * CRYSTAL_STRENGTH;

          velocities[i * 3] *= CRYSTAL_DAMPING;
          velocities[i * 3 + 1] *= CRYSTAL_DAMPING;
          velocities[i * 3 + 2] *= CRYSTAL_DAMPING;
          inCrystalZone = true;
        }
      }

      if (crystalExpiredThisFrame && crystallizedFlags[i]) {
        const index = i * 3;
        let vx = -arrivalVelocities[index] * CRYSTAL_EJECTION_MULTIPLIER;
        let vy = -arrivalVelocities[index + 1] * CRYSTAL_EJECTION_MULTIPLIER;
        let vz = -arrivalVelocities[index + 2] * CRYSTAL_EJECTION_MULTIPLIER;

        const speed = Math.sqrt(vx * vx + vy * vy + vz * vz);
        if (speed < CRYSTAL_MIN_EJECTION_SPEED) {
          let dirX = x - crystalCenterRef.current.x;
          let dirY = y - crystalCenterRef.current.y;
          let dirZ = z - crystalCenterRef.current.z;
          let dirMag = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ);
          if (dirMag === 0) {
            dirX = Math.random() - 0.5;
            dirY = Math.random() - 0.5;
            dirZ = Math.random() - 0.5;
            dirMag = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ) || 1;
          }
          const scale = CRYSTAL_MIN_EJECTION_SPEED / dirMag;
          vx = dirX * scale;
          vy = dirY * scale;
          vz = dirZ * scale;
        }

        velocities[index] = vx;
        velocities[index + 1] = vy;
        velocities[index + 2] = vz;
        arrivalVelocities[index] = 0;
        arrivalVelocities[index + 1] = 0;
        arrivalVelocities[index + 2] = 0;
        crystallizedFlags[i] = 0;
        justEjected = true;
      }

      if (!inCrystalZone && !justEjected) {
        if (heroHoveredRef.current) {
          // All dots snap to hex grid when app name is hovered
          const [q, r] = pixelToHex(x, y);
          const [rq, rr] = axialRound(q, r);
          const [targetX, targetY] = hexToPixel(rq, rr);

          velocities[i * 3] += (targetX - x) * GRID_SNAP_STRENGTH;
          velocities[i * 3 + 1] += (targetY - y) * GRID_SNAP_STRENGTH;
          velocities[i * 3 + 2] += (0 - z) * GRID_SNAP_STRENGTH;

          velocities[i * 3] *= GRID_SNAP_DAMPING;
          velocities[i * 3 + 1] *= GRID_SNAP_DAMPING;
          velocities[i * 3 + 2] *= GRID_SNAP_DAMPING;
        } else if (distToMouse < CURSOR_RADIUS) {
          const [q, r] = pixelToHex(x, y);
          const [rq, rr] = axialRound(q, r);
          const [targetX, targetY] = hexToPixel(rq, rr);

          velocities[i * 3] += (targetX - x) * ATTRACTION_STRENGTH;
          velocities[i * 3 + 1] += (targetY - y) * ATTRACTION_STRENGTH;
          velocities[i * 3 + 2] += (0 - z) * ATTRACTION_STRENGTH;

          velocities[i * 3] *= 0.8;
          velocities[i * 3 + 1] *= 0.8;
          velocities[i * 3 + 2] *= 0.8;
        } else {
          velocities[i * 3] += (Math.random() - 0.5) * 0.0005;
          velocities[i * 3 + 1] += (Math.random() - 0.5) * 0.0005;
          velocities[i * 3 + 2] += (Math.random() - 0.5) * 0.0005;

          velocities[i * 3] = Math.max(
            Math.min(velocities[i * 3], MOVEMENT_SPEED * 5),
            -MOVEMENT_SPEED * 5,
          );
          velocities[i * 3 + 1] = Math.max(
            Math.min(velocities[i * 3 + 1], MOVEMENT_SPEED * 5),
            -MOVEMENT_SPEED * 5,
          );
          velocities[i * 3 + 2] = Math.max(
            Math.min(velocities[i * 3 + 2], MOVEMENT_SPEED * 5),
            -MOVEMENT_SPEED * 5,
          );
        }
      }

      x += velocities[i * 3];
      y += velocities[i * 3 + 1];
      z += velocities[i * 3 + 2];

      if (x > 12.5) x = -12.5;
      if (x < -12.5) x = 12.5;
      if (y > 12.5) y = -12.5;
      if (y < -12.5) y = 12.5;
      if (z > 5) z = -5;
      if (z < -5) z = 5;

      currentPositions[i * 3] = x;
      currentPositions[i * 3 + 1] = y;
      currentPositions[i * 3 + 2] = z;

      for (let j = i + 1; j < COUNT; j++) {
        const dx = currentPositions[i * 3] - currentPositions[j * 3];
        const dy = currentPositions[i * 3 + 1] - currentPositions[j * 3 + 1];
        const dz = currentPositions[i * 3 + 2] - currentPositions[j * 3 + 2];
        const distSq = dx * dx + dy * dy + dz * dz;

        if (distSq < CONNECTION_DISTANCE * CONNECTION_DISTANCE) {
          currentLinePositions[vertexIndex++] = currentPositions[i * 3];
          currentLinePositions[vertexIndex++] = currentPositions[i * 3 + 1];
          currentLinePositions[vertexIndex++] = currentPositions[i * 3 + 2];

          currentLinePositions[vertexIndex++] = currentPositions[j * 3];
          currentLinePositions[vertexIndex++] = currentPositions[j * 3 + 1];
          currentLinePositions[vertexIndex++] = currentPositions[j * 3 + 2];
        }
      }
    }

    pointsRef.current.geometry.attributes.position.needsUpdate = true;

    linesRef.current.geometry.setDrawRange(0, vertexIndex / 3);
    linesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <>
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={COUNT}
            array={positions}
            itemSize={3}
            args={[positions, 3]}
          />
        </bufferGeometry>
        <pointsMaterial
          size={POINT_SIZE}
          color="#555555"
          transparent
          opacity={0.8}
          map={texture}
          alphaTest={0.5}
          sizeAttenuation={true}
        />
      </points>

      <lineSegments ref={linesRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={linePositions.length / 3}
            array={linePositions}
            itemSize={3}
            args={[linePositions, 3]}
          />
        </bufferGeometry>
        <lineBasicMaterial color="#888888" transparent opacity={0.15} />
      </lineSegments>
    </>
  );
}

export function InteractiveBackground({
  washOut: washOutBackground = true,
}: {
  washOut?: boolean;
}) {
  const [ready, setReady] = useState(false);
  const [heroHovered, setHeroHovered] = useState(false);
  const onCreated = useCallback(() => setReady(true), []);

  useEffect(() => {
    const heroElement = document.getElementById(HERO_ELEMENT_ID);
    if (!heroElement) return;

    const onEnter = () => setHeroHovered(true);
    const onLeave = () => setHeroHovered(false);

    heroElement.addEventListener('mouseenter', onEnter);
    heroElement.addEventListener('mouseleave', onLeave);
    return () => {
      heroElement.removeEventListener('mouseenter', onEnter);
      heroElement.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <div
      className={cn(
        'fixed inset-0 z-0 transition-colors duration-700 ease-in',
        ready && !washOutBackground ? 'bg-[#f0ede5]' : 'bg-white',
      )}
    >
      <div
        className={cn(
          'size-full transition-opacity duration-1000 ease-in',
          ready && !washOutBackground ? 'opacity-100' : 'opacity-0',
        )}
      >
        <Canvas
          camera={{ position: [0, 0, 10], fov: 60 }}
          dpr={[1, 2]}
          onCreated={onCreated}
        >
          <Particles heroHovered={heroHovered} />
        </Canvas>
      </div>
    </div>
  );
}
